import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { ThemeContext } from './themeContext';

/* -----------------------------------------------------------------------------
 * Two separate settings, on purpose.
 *
 *   preset  — the company's visual identity. Admin-only. One value for everyone,
 *             stored in `app_settings` on the server.
 *   scheme  — light or dark. Personal. Every user picks their own, because a
 *             storekeeper under fluorescent lights and a supervisor on a night
 *             shift do not want the same screen.
 * -------------------------------------------------------------------------- */

/* Nine of these. Each carries a `radius` as well as a palette, because the
   presets differ in geometry too — see the header of styles/theme.css. The value
   here is only for the picker's preview swatches; the truth is the --radius
   token in that file, and the two are kept in step by a test. */
export const THEME_PRESETS = [
  {
    id: 'hazel',
    name: 'Hazel Green',
    description: 'NoorOne brand identity',
    swatch: ['#4B6B4F', '#6DBF6D', '#DCC89A'],
    radius: 7,
  },
  {
    id: 'desert',
    name: 'Desert Gold',
    description: 'Warm sand tones',
    swatch: ['#8A6C28', '#DCC89A', '#4B6B4F'],
    radius: 7,
  },
  {
    id: 'steel',
    name: 'Steel Blue',
    description: 'Cool and low-glare',
    swatch: ['#2F5D78', '#6FA6C7', '#B08D3C'],
    radius: 7,
  },
  {
    id: 'indigo',
    name: 'Midnight Indigo',
    description: 'Deep blue-violet, soft corners',
    swatch: ['#4F46E5', '#818CF8', '#0E9AAE'],
    radius: 12,
  },
  {
    id: 'graphite',
    name: 'Graphite',
    description: 'Neutral and architectural',
    swatch: ['#3F3F46', '#A1A1AA', '#0E7490'],
    radius: 4,
  },
  {
    id: 'marine',
    name: 'Marine Teal',
    description: 'Deep sea-green with the brand gold',
    swatch: ['#0F766E', '#2DB3A5', '#B08D3C'],
    radius: 8,
  },
  {
    id: 'plum',
    name: 'Royal Plum',
    description: 'Aubergine and tan, the most formal',
    swatch: ['#7E22CE', '#C084FC', '#A9713C'],
    radius: 14,
  },
  {
    id: 'bronze',
    name: 'Bronze',
    description: 'Warm copper, cool teal accent',
    swatch: ['#7A4B2A', '#D2A67C', '#17756A'],
    radius: 8,
  },
  {
    id: 'orchid',
    name: 'Orchid',
    description: 'Magenta, the brightest and softest',
    swatch: ['#B4257E', '#E98FC4', '#3F7D8C'],
    radius: 16,
  },
] as const;

export type ThemePreset = (typeof THEME_PRESETS)[number]['id'];

export type ColorScheme = 'light' | 'dark' | 'system';

/**
 * Narrows a value read from the database or from storage.
 *
 * The column is `text`; the application treats it as three words. Nothing
 * enforced the gap until the Supabase client was given the real schema, and
 * then the compiler pointed straight at four places assigning a string to the
 * union and hoping. A row holding anything else — an old value, a hand-written
 * update — would have been carried into code that switches on it.
 *
 * The same shape as isLanguage, which has been doing this for locale all along.
 */
export function isColorScheme(value: unknown): value is ColorScheme {
  return value === 'light' || value === 'dark' || value === 'system';
}
type ResolvedScheme = 'light' | 'dark';

const PRESET_KEY = 'noorone.theme';

/* One person's own choice, on one device.
   -----------------------------------------------------------------------------
   The company preset is the company's identity and an administrator sets it for
   everybody. This overrides it for whoever set it, and for nobody else.

   Worth having for the reason the UAE government sites have it: somebody who
   cannot read green on green, or who finds a palette hard on their eyes at four
   in the morning on a night shift, should not have to ask an administrator to
   change the whole company's colours. It sits beside light and dark, which have
   always been personal for exactly the same reason.

   localStorage rather than the profile: it is a preference about this screen on
   this phone, it does not need to follow anybody between devices, and storing it
   server-side would make an accessibility choice into a record about a person. */
const PERSONAL_KEY = 'noorone.theme.personal';
const SCHEME_KEY = 'noorone.scheme';
const VALID_PRESETS = THEME_PRESETS.map((p) => p.id) as readonly string[];

/**
 * Narrows a value read from the database or from storage.
 *
 * Both were already checking against VALID_PRESETS and then casting. The cast
 * is what this removes: the check and the type now come from one place, so a
 * preset added to THEME_PRESETS is admitted everywhere without a second edit.
 */
export function isThemePreset(value: unknown): value is ThemePreset {
  return typeof value === 'string' && VALID_PRESETS.includes(value);
}

/** What an unset or unrecognised preset falls back to — in storage and in the
    profile loader both, so the two cannot drift apart. */
export const DEFAULT_PRESET: ThemePreset = 'hazel';

export interface ThemeContextValue {
  /** What is on screen: the personal choice where there is one, else the company's. */
  preset: ThemePreset;
  /** The company's own, which an administrator sets for everybody. */
  companyPreset: ThemePreset;
  /** This person's override, or null when they are following the company. */
  personalPreset: ThemePreset | null;
  setPersonalPreset: (preset: ThemePreset | null) => void;
  /** The user's stored choice, which may be `system`. */
  scheme: ColorScheme;
  /** What is actually on screen right now — never `system`. */
  resolvedScheme: ResolvedScheme;
  setPreset: (preset: ThemePreset) => void;
  setScheme: (scheme: ColorScheme) => void;
}

/* The context object lives in ./themeContext, so its identity survives this
   module being re-evaluated by a hot reload. See src/core/i18nContext.ts. */


function readStoredPreset(): ThemePreset {
  try {
    const stored = localStorage.getItem(PRESET_KEY);
    if (isThemePreset(stored)) return stored;
  } catch {
    /* storage unavailable */
  }
  return DEFAULT_PRESET;
}

function readPersonalPreset(): ThemePreset | null {
  try {
    const stored = localStorage.getItem(PERSONAL_KEY);
    if (isThemePreset(stored)) return stored;
  } catch {
    /* storage unavailable */
  }
  return null;
}

function readStoredScheme(): ColorScheme {
  try {
    const stored = localStorage.getItem(SCHEME_KEY);
    if (stored === 'light' || stored === 'dark' || stored === 'system') return stored;
  } catch {
    /* storage unavailable */
  }
  return 'system';
}

function systemScheme(): ResolvedScheme {
  return window.matchMedia?.('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [companyPreset, setPresetState] = useState<ThemePreset>(readStoredPreset);
  const [personalPreset, setPersonalState] = useState<ThemePreset | null>(readPersonalPreset);

  /* The personal choice wins, and the company's is what it falls back to. So an
     administrator changing the company theme changes it for everybody who has
     not expressed a preference, and for nobody who has — which is the whole
     point of the override. */
  const preset = personalPreset ?? companyPreset;
  const [scheme, setSchemeState] = useState<ColorScheme>(readStoredScheme);
  const [systemPref, setSystemPref] = useState<ResolvedScheme>(systemScheme);

  // Follow the OS live, so a phone switching to dark at sunset takes the app
  // with it without a reload.
  useEffect(() => {
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = (e: MediaQueryListEvent) => setSystemPref(e.matches ? 'dark' : 'light');
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  const resolvedScheme: ResolvedScheme = scheme === 'system' ? systemPref : scheme;

  useEffect(() => {
    const root = document.documentElement;
    root.setAttribute('data-theme', preset);
    root.setAttribute('data-scheme', resolvedScheme);
    // Keeps native form controls and the Android status bar in step.
    root.style.colorScheme = resolvedScheme;
  }, [preset, resolvedScheme]);

  const setPreset = useCallback((next: ThemePreset) => {
    setPresetState(next);
    try {
      localStorage.setItem(PRESET_KEY, next);
    } catch {
      /* ignore */
    }
  }, []);

  const setPersonalPreset = useCallback((next: ThemePreset | null) => {
    setPersonalState(next);
    try {
      if (next) localStorage.setItem(PERSONAL_KEY, next);
      else localStorage.removeItem(PERSONAL_KEY);
    } catch {
      /* ignore */
    }
  }, []);

  const setScheme = useCallback((next: ColorScheme) => {
    setSchemeState(next);
    try {
      localStorage.setItem(SCHEME_KEY, next);
    } catch {
      /* ignore */
    }
  }, []);

  const value = useMemo(
    () => ({
      preset, companyPreset, personalPreset,
      scheme, resolvedScheme,
      setPreset, setPersonalPreset, setScheme,
    }),
    [preset, companyPreset, personalPreset, scheme, resolvedScheme,
     setPreset, setPersonalPreset, setScheme],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be used inside <ThemeProvider>');
  return ctx;
}

/**
 * Reads a themed CSS variable as a concrete colour string.
 * Recharts needs real values, not `var(--chart-1)`.
 */
export function themeColor(name: string): string {
  if (typeof window === 'undefined') return '#4B6B4F';
  return getComputedStyle(document.documentElement).getPropertyValue(`--${name}`).trim() || '#4B6B4F';
}
